// src/api/tasas.js - API para manejo de tasas de cambio
import { apiFetch, API_CONFIG } from './index.js';

/**
 * Obtener todas las tasas
 * @param {string} estado - 'all', 'activas', 'inactivas', 'activo', 'inactivo'
 * @returns {Promise} Lista de tasas
 */
export const obtenerTasas = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo tasas de cambio...', { estado });
    
    let endpoint = '/TasasCambio';
    
    // Usar endpoint específico según el estado
    if (estado === 'activas') {
      endpoint = '/TasasCambio/lista-activas';
    } else if (estado === 'inactivas') {
      endpoint = '/TasasCambio/inactivas';
    }
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una tasa por ID
 * @param {number} id - ID de la tasa
 * @returns {Promise} Datos de la tasa
 */
export const obtenerTasaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔍 Obteniendo tasa ID: ${id}`);
    
    const response = await apiFetch(`/TasasCambio/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Tasa obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener tasa activa actual
 * @returns {Promise} Tasa activa actual
 */
export const obtenerTasaActiva = async () => {
  try {
    console.log('💰 Obteniendo tasa activa actual...');
    
    const response = await apiFetch('/TasasCambio/actual');
    
    return {
      success: true,
      data: response,
      message: 'Tasa activa obtenida exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasa activa:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si una tasa existe por fecha y fuente
 * @param {string} fecha - Fecha a verificar (formato ISO)
 * @param {string} fuente - Fuente de la tasa
 * @returns {Promise} Resultado de la verificación
 */
export const verificarTasaPorFechaFuente = async (fecha, fuente) => {
  try {
    if (!fecha || !fuente) {
      throw new Error('Fecha y fuente son requeridos');
    }
    
    console.log(`🔎 Verificando tasa para fecha: "${fecha}", fuente: "${fuente}"`);
    
    // Primero verificamos por fecha
    const fechaResponse = await apiFetch(`/TasasCambio/verificar-fecha/${encodeURIComponent(fecha)}`);
    
    if (!fechaResponse) {
      return {
        success: true,
        existe: false,
        mensaje: 'Fecha disponible para nueva tasa'
      };
    }
    
    // Si hay tasas para esa fecha, verificamos si alguna coincide con la fuente
    // Para esto necesitamos obtener tasas por rango de fechas
    const fechaObj = new Date(fecha);
    const fechaInicio = new Date(fechaObj);
    fechaInicio.setHours(0, 0, 0, 0);
    const fechaFin = new Date(fechaObj);
    fechaFin.setHours(23, 59, 59, 999);
    
    const tasasResponse = await obtenerTasasPorRangoFechas(fechaInicio.toISOString(), fechaFin.toISOString());
    
    if (tasasResponse.success) {
      const existe = (tasasResponse.data || []).some(tasa => 
        tasa.fuente?.toLowerCase() === fuente.toLowerCase()
      );
      
      return {
        success: true,
        existe,
        mensaje: existe ? `Ya existe una tasa de ${fuente} para esta fecha` : 'Fecha y fuente disponibles'
      };
    }
    
    return {
      success: true,
      existe: false,
      mensaje: 'Fecha disponible para nueva tasa'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando tasa "${fecha} - ${fuente}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Obtener tasas por rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @returns {Promise} Tasas en el rango
 */
export const obtenerTasasPorRangoFechas = async (fechaInicio, fechaFin) => {
  try {
    if (!fechaInicio || !fechaFin) {
      throw new Error('Fechas de inicio y fin son requeridas');
    }
    
    console.log(`📅 Obteniendo tasas del ${fechaInicio} al ${fechaFin}`);
    
    // Formatear fechas para la URL
    const inicio = new Date(fechaInicio).toISOString().split('T')[0];
    const fin = new Date(fechaFin).toISOString().split('T')[0];
    
    const response = await apiFetch(`/TasasCambio/rango-fechas?fechaInicio=${encodeURIComponent(inicio)}&fechaFin=${encodeURIComponent(fin)}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas por rango:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasas por fuente
 * @param {string} fuente - Fuente de la tasa (BCV, USDT, etc.)
 * @returns {Promise} Tasas de la fuente
 */
export const obtenerTasasPorFuente = async (fuente) => {
  try {
    if (!fuente) {
      throw new Error('Fuente es requerida');
    }
    
    console.log(`🌐 Obteniendo tasas de fuente: ${fuente}`);
    
    const response = await apiFetch(`/TasasCambio/fuente/${encodeURIComponent(fuente)}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas de fuente ${fuente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasas por usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Tasas creadas por el usuario
 */
export const obtenerTasasPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`👤 Obteniendo tasas del usuario ID: ${idUsuario}`);
    
    const response = await apiFetch(`/TasasCambio/usuario/${idUsuario}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas del usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasas por fecha específica
 * @param {string} fecha - Fecha específica (YYYY-MM-DD)
 * @returns {Promise} Tasas de esa fecha
 */
export const obtenerTasasPorFecha = async (fecha) => {
  try {
    if (!fecha) {
      throw new Error('Fecha es requerida');
    }
    
    console.log(`📅 Obteniendo tasas para fecha: ${fecha}`);
    
    // Usar rango de fechas con el mismo día
    const fechaObj = new Date(fecha);
    const fechaInicio = new Date(fechaObj);
    fechaInicio.setHours(0, 0, 0, 0);
    const fechaFin = new Date(fechaObj);
    fechaFin.setHours(23, 59, 59, 999);
    
    return await obtenerTasasPorRangoFechas(
      fechaInicio.toISOString(),
      fechaFin.toISOString()
    );
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas por fecha ${fecha}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Crear una nueva tasa
 * @param {Object} tasaData - Datos de la tasa
 * @returns {Promise} Tasa creada
 */
export const crearTasa = async (tasaData) => {
  try {
    // ¡IMPORTANTE: NO validar fecha aquí! El backend la genera automáticamente
    // Validar solo los campos realmente requeridos
    if (!tasaData.tasaCompra || tasaData.tasaCompra <= 0) {
      throw new Error('La tasa de compra es requerida y debe ser mayor a 0');
    }
    if (!tasaData.tasaVenta || tasaData.tasaVenta <= 0) {
      throw new Error('La tasa de venta es requerida y debe ser mayor a 0');
    }
    if (!tasaData.fuente || tasaData.fuente.trim() === '') {
      throw new Error('La fuente es requerida');
    }
    if (!tasaData.idUsuario) {
      throw new Error('El usuario es requerido');
    }
    
    console.log('➕ Creando nueva tasa:', tasaData);
    
    const response = await apiFetch('/TasasCambio', {
      method: 'POST',
      body: JSON.stringify({
        // ¡NO enviar fecha! El backend la genera automáticamente
        tasaCompra: parseFloat(tasaData.tasaCompra),
        tasaVenta: parseFloat(tasaData.tasaVenta),
        fuente: tasaData.fuente.trim(),
        tipoTasa: tasaData.tipoTasa || 'BCV',
        activa: tasaData.activa !== undefined ? tasaData.activa : true,
        idUsuario: parseInt(tasaData.idUsuario),
        observaciones: tasaData.observaciones || null
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando tasa:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una tasa existente
 * @param {number} id - ID de la tasa
 * @param {Object} tasaData - Datos a actualizar
 * @returns {Promise} Tasa actualizada
 */
export const actualizarTasa = async (id, tasaData) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    // ¡IMPORTANTE: NO validar fecha aquí! No se debe enviar al actualizar
    if (!tasaData.tasaCompra || tasaData.tasaCompra <= 0) {
      throw new Error('La tasa de compra es requerida y debe ser mayor a 0');
    }
    if (!tasaData.tasaVenta || tasaData.tasaVenta <= 0) {
      throw new Error('La tasa de venta es requerida y debe ser mayor a 0');
    }
    if (!tasaData.fuente || tasaData.fuente.trim() === '') {
      throw new Error('La fuente es requerida');
    }
    
    console.log(`✏️ Actualizando tasa ID: ${id}`, tasaData);
    
    // Para actualizar, solo enviamos los campos que queremos cambiar
    // ¡NO enviar fecha! Mantiene la fecha original
    const updateData = {
      tasaCompra: parseFloat(tasaData.tasaCompra),
      tasaVenta: parseFloat(tasaData.tasaVenta),
      fuente: tasaData.fuente.trim(),
      tipoTasa: tasaData.tipoTasa || 'BCV',
      activa: tasaData.activa !== undefined ? tasaData.activa : true
    };
    
    // Campos opcionales
    if (tasaData.idUsuario) {
      updateData.idUsuario = parseInt(tasaData.idUsuario);
    }
    if (tasaData.observaciones !== undefined) {
      updateData.observaciones = tasaData.observaciones || null;
    }
    
    console.log('📤 Datos enviados para actualizar:', updateData);
    
    const response = await apiFetch(`/TasasCambio/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una tasa
 * @param {number} id - ID de la tasa a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTasa = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🗑️ Eliminando tasa ID: ${id}`);
    
    const response = await apiFetch(`/TasasCambio/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una tasa
 * @param {number} id - ID de la tasa
 * @param {boolean} activa - Nuevo estado activo/inactivo
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoTasa = async (id, activa) => {
  try {
    if (!id || activa === undefined) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de tasa ID: ${id} a ${activa ? 'activa' : 'inactiva'}`);
    
    const response = await apiFetch(`/TasasCambio/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({
        activa: activa
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Estado de tasa actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar una tasa
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la activación
 */
export const activarTasa = async (id) => {
  return await cambiarEstadoTasa(id, true);
};

/**
 * Desactivar una tasa
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarTasa = async (id) => {
  return await cambiarEstadoTasa(id, false);
};

/**
 * Convertir moneda usando tasa activa
 * @param {number} monto - Monto a convertir
 * @param {string} direccion - 'compra' o 'venta'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMoneda = async (monto, direccion = 'compra') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    
    console.log(`💱 Convirtiendo ${monto} (dirección: ${direccion})`);
    
    const response = await apiFetch('/TasasCambio/convertir', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tipoOperacion: direccion // Nota: el backend espera 'tipoOperacion' no 'direccion'
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión realizada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Convertir moneda con tasa personalizada
 * @param {number} monto - Monto a convertir
 * @param {number} tasa - Tasa personalizada
 * @param {string} direccion - 'compra' o 'venta'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMonedaPersonalizado = async (monto, tasa, direccion = 'compra') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    if (!tasa || tasa <= 0) {
      throw new Error('Tasa válida es requerida');
    }
    
    console.log(`💱 Convirtiendo ${monto} con tasa personalizada ${tasa} (dirección: ${direccion})`);
    
    const response = await apiFetch('/TasasCambio/convertir-personalizado', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tipoOperacion: direccion,
        tasaPersonalizada: parseFloat(tasa)
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión personalizada realizada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda personalizada:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener resumen de tasas
 * @returns {Promise} Resumen estadístico
 */
export const obtenerResumenTasas = async () => {
  try {
    console.log('📊 Obteniendo resumen de tasas...');
    
    const response = await apiFetch('/TasasCambio/resumen');
    
    return {
      success: true,
      data: response,
      message: 'Resumen obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo resumen:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener todas las fuentes disponibles
 * @returns {Promise} Lista de fuentes
 */
export const obtenerFuentesTasas = async () => {
  try {
    console.log('🌐 Obteniendo fuentes de tasas...');
    
    const response = await apiFetch('/TasasCambio/fuentes');
    
    return {
      success: true,
      data: response,
      message: 'Fuentes obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo fuentes:', error);
    return {
      success: false,
      error: error.message,
      data: ['BCV', 'USDT', 'Paralelo', 'Dólar Today', 'EnParaleloVzla'] // Valores por defecto
    };
  }
};

/**
 * Manejar la edición de una tasa con desactivación inteligente
 * @param {number} id - ID de la tasa
 * @param {Object} tasaData - Datos actualizados
 * @returns {Promise} Resultado de la operación
 */
export const editarTasaConDesactivacion = async (id, tasaData) => {
  try {
    // Primero obtenemos la tasa actual
    const tasaActualResponse = await obtenerTasaPorId(id);
    
    if (!tasaActualResponse.success) {
      throw new Error('No se pudo obtener la tasa actual');
    }
    
    const tasaActual = tasaActualResponse.data;
    
    // Si estamos cambiando el estado a activo y estaba inactiva
    if (tasaData.activa === true && tasaActual.activa === false) {
      try {
        // Intentamos activarla
        return await actualizarTasa(id, tasaData);
      } catch (error) {
        // Si hay error por límite de tasas activas
        if (error.message && error.message.includes('No se puede activar más tasas')) {
          // Preguntar al usuario si quiere desactivar otras
          const confirmarDesactivacion = window.confirm(
            'Ya existe el límite máximo de tasas activas. ¿Desea desactivar las tasas activas actuales para activar esta?'
          );
          
          if (confirmarDesactivacion) {
            // Obtener todas las tasas activas
            const tasasActivasResponse = await obtenerTasas('activas');
            if (tasasActivasResponse.success && tasasActivasResponse.data.length > 0) {
              // Desactivar todas las tasas activas existentes
              for (const tasa of tasasActivasResponse.data) {
                if (tasa.idTasa !== id) { // No desactivar la que estamos editando
                  await desactivarTasa(tasa.idTasa);
                }
              }
            }
            
            // Ahora actualizar la tasa
            return await actualizarTasa(id, tasaData);
          } else {
            throw new Error('Operación cancelada por el usuario');
          }
        } else {
          throw error;
        }
      }
    } else {
      // Para otros casos, actualizar normalmente
      return await actualizarTasa(id, tasaData);
    }
  } catch (error) {
    console.error(`❌ Error editando tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Manejar la eliminación de una tasa con verificación
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTasaConVerificacion = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    // Primero obtenemos la tasa para mostrar información
    const tasaResponse = await obtenerTasaPorId(id);
    
    if (!tasaResponse.success) {
      throw new Error('No se pudo obtener la tasa para eliminar');
    }
    
    const tasa = tasaResponse.data;
    
    // Mostrar confirmación con detalles
    const confirmacion = window.confirm(
      `¿Está seguro de eliminar la tasa del ${tasa.fecha}?\n` +
      `Fuente: ${tasa.fuente}\n` +
      `Compra: ${tasa.tasaCompra} | Venta: ${tasa.tasaVenta}\n\n` +
      `Esta acción no se puede deshacer.`
    );
    
    if (!confirmacion) {
      return {
        success: false,
        error: 'Operación cancelada por el usuario',
        data: null
      };
    }
    
    // Intentar eliminar
    return await eliminarTasa(id);
    
  } catch (error) {
    console.error(`❌ Error eliminando tasa ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Formatear fecha de tasa
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaTasa = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
      opciones.second = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {boolean} activa - Estado de la tasa
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (activa) => {
  return activa ? 'Activa' : 'Inactiva';
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activa' || estado === 'true';
};

/**
 * Formatear monto de tasa (número)
 * @param {number} monto - Monto a formatear
 * @param {number} decimales - Número de decimales
 * @returns {string} Monto formateado
 */
export const formatearMonto = (monto, decimales = 2) => {
  if (monto === null || monto === undefined) return 'N/A';
  return parseFloat(monto).toFixed(decimales).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Formatear tasa con símbolo
 * @param {number} tasa - Valor de la tasa
 * @returns {string} Tasa formateada
 */
export const formatearTasa = (tasa) => {
  if (tasa === null || tasa === undefined) return 'N/A';
  return `Bs ${formatearMonto(tasa)}`;
};

/**
 * Calcular diferencia porcentual entre dos tasas
 * @param {number} tasa1 - Tasa inicial
 * @param {number} tasa2 - Tasa final
 * @returns {number} Diferencia porcentual
 */
export const calcularDiferenciaPorcentual = (tasa1, tasa2) => {
  if (!tasa1 || !tasa2 || tasa1 === 0) return 0;
  return ((tasa2 - tasa1) / tasa1) * 100;
};

/**
 * Formatear diferencia porcentual
 * @param {number} diferencia - Diferencia porcentual
 * @returns {string} Diferencia formateada con signo
 */
export const formatearDiferencia = (diferencia) => {
  if (diferencia === null || diferencia === undefined) return '';
  const signo = diferencia >= 0 ? '+' : '';
  return `${signo}${diferencia.toFixed(2)}%`;
};

/**
 * Formatear observaciones (cortar si es muy larga)
 * @param {string} observaciones - Observaciones de la tasa
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Observaciones formateadas
 */
export const formatearObservaciones = (observaciones, maxLength = 100) => {
  if (!observaciones) return 'Sin observaciones';
  if (observaciones.length <= maxLength) return observaciones;
  return observaciones.substring(0, maxLength) + '...';
};

/**
 * Buscar tasas por término
 * @param {Array} tasas - Lista de tasas
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Tasas filtradas
 */
export const buscarTasas = (tasas, termino) => {
  if (!termino || termino.trim() === '') {
    return tasas;
  }
  
  const terminoLower = termino.toLowerCase();
  return tasas.filter(tasa => 
    (tasa.fuente || '').toLowerCase().includes(terminoLower) ||
    formatearMonto(tasa.tasaCompra)?.toLowerCase().includes(terminoLower) ||
    formatearMonto(tasa.tasaVenta)?.toLowerCase().includes(terminoLower) ||
    formatearFechaTasa(tasa.fecha, true)?.toLowerCase().includes(terminoLower) ||
    (tasa.usuarioNombre || '').toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(tasa.activa)?.toLowerCase().includes(terminoLower) ||
    (tasa.observaciones || '').toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar tasas por estado
 * @param {Array} tasas - Lista de tasas
 * @param {string} estado - 'all', 'activas', 'inactivas'
 * @returns {Array} Tasas filtradas
 */
export const filtrarTasasPorEstado = (tasas, estado) => {
  if (estado === 'all') return tasas;
  if (estado === 'activas') return tasas.filter(t => t.activa === true);
  if (estado === 'inactivas') return tasas.filter(t => t.activa === false);
  return tasas;
};

/**
 * Filtrar tasas por fuente
 * @param {Array} tasas - Lista de tasas
 * @param {string} fuente - Fuente específica
 * @returns {Array} Tasas filtradas
 */
export const filtrarTasasPorFuente = (tasas, fuente) => {
  if (!fuente || fuente === 'all') return tasas;
  return tasas.filter(t => (t.fuente || '').toLowerCase() === fuente.toLowerCase());
};

/**
 * Filtrar tasas por usuario
 * @param {Array} tasas - Lista de tasas
 * @param {number} idUsuario - ID del usuario
 * @returns {Array} Tasas filtradas
 */
export const filtrarTasasPorUsuario = (tasas, idUsuario) => {
  if (!idUsuario) return tasas;
  return tasas.filter(t => t.idUsuario === idUsuario);
};

/**
 * Filtrar tasas por fecha
 * @param {Array} tasas - Lista de tasas
 * @param {string} fecha - Fecha específica (YYYY-MM-DD)
 * @returns {Array} Tasas filtradas
 */
export const filtrarTasasPorFecha = (tasas, fecha) => {
  if (!fecha) return tasas;
  return tasas.filter(t => {
    if (!t.fecha) return false;
    const tasaFecha = new Date(t.fecha).toISOString().split('T')[0];
    return tasaFecha === fecha;
  });
};

/**
 * Obtener estadísticas de tasas
 * @param {Array} tasas - Lista de tasas
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasTasas = (tasas) => {
  const activas = tasas.filter(t => t.activa === true).length;
  const inactivas = tasas.filter(t => t.activa === false).length;
  
  // Tasas por fuente
  const porFuente = tasas.reduce((acc, tasa) => {
    const fuente = tasa.fuente || 'Sin fuente';
    acc[fuente] = (acc[fuente] || 0) + 1;
    return acc;
  }, {});
  
  // Promedio de tasas de compra y venta
  const promedioCompra = tasas.length > 0 
    ? tasas.reduce((sum, t) => sum + (t.tasaCompra || 0), 0) / tasas.length
    : 0;
    
  const promedioVenta = tasas.length > 0 
    ? tasas.reduce((sum, t) => sum + (t.tasaVenta || 0), 0) / tasas.length
    : 0;
  
  // Tasa más alta y más baja
  const tasasCompra = tasas.map(t => t.tasaCompra).filter(Boolean);
  const tasasVenta = tasas.map(t => t.tasaVenta).filter(Boolean);
  
  const maxCompra = tasasCompra.length > 0 ? Math.max(...tasasCompra) : 0;
  const minCompra = tasasCompra.length > 0 ? Math.min(...tasasCompra) : 0;
  const maxVenta = tasasVenta.length > 0 ? Math.max(...tasasVenta) : 0;
  const minVenta = tasasVenta.length > 0 ? Math.min(...tasasVenta) : 0;
  
  // Tasas recientes (últimos 7 días)
  const sieteDiasAtras = new Date();
  sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
  const recientes = tasas.filter(t => {
    try {
      const fechaCreacion = new Date(t.fechaCreacion || t.fecha || 0);
      return fechaCreacion >= sieteDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  // Usuarios que han creado tasas
  const usuariosUnicos = [...new Set(tasas.map(t => t.idUsuario).filter(Boolean))];
  
  // Diferencias entre compra y venta
  const tasasConSpread = tasas.filter(t => t.tasaCompra && t.tasaVenta);
  const spreadPromedio = tasasConSpread.length > 0
    ? tasasConSpread.reduce((sum, t) => sum + (t.tasaVenta - t.tasaCompra), 0) / tasasConSpread.length
    : 0;
  
  return {
    total: tasas.length,
    activas,
    inactivas,
    porcentajeActivas: tasas.length > 0 ? Math.round((activas / tasas.length) * 100) : 0,
    porcentajeInactivas: tasas.length > 0 ? Math.round((inactivas / tasas.length) * 100) : 0,
    porFuente,
    promedioCompra: parseFloat(promedioCompra.toFixed(2)),
    promedioVenta: parseFloat(promedioVenta.toFixed(2)),
    maxCompra,
    minCompra,
    maxVenta,
    minVenta,
    spreadPromedio: parseFloat(spreadPromedio.toFixed(2)),
    recientes,
    porcentajeRecientes: tasas.length > 0 ? Math.round((recientes / tasas.length) * 100) : 0,
    usuariosUnicos: usuariosUnicos.length,
    diferenciaPromedio: parseFloat((promedioVenta - promedioCompra).toFixed(2)),
    porcentajeSpreadPromedio: promedioCompra > 0 ? parseFloat(((spreadPromedio / promedioCompra) * 100).toFixed(2)) : 0
  };
};

/**
 * Ordenar tasas por diferentes criterios
 * @param {Array} tasas - Lista de tasas
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Tasas ordenadas
 */
export const ordenarTasas = (tasas, criterio = 'fecha_desc') => {
  const tasasCopy = [...tasas];
  
  switch (criterio) {
    case 'fecha_desc':
      return tasasCopy.sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
    case 'fecha':
      return tasasCopy.sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
    case 'tasaCompra_desc':
      return tasasCopy.sort((a, b) => (b.tasaCompra || 0) - (a.tasaCompra || 0));
    case 'tasaCompra':
      return tasasCopy.sort((a, b) => (a.tasaCompra || 0) - (b.tasaCompra || 0));
    case 'tasaVenta_desc':
      return tasasCopy.sort((a, b) => (b.tasaVenta || 0) - (a.tasaVenta || 0));
    case 'tasaVenta':
      return tasasCopy.sort((a, b) => (a.tasaVenta || 0) - (b.tasaVenta || 0));
    case 'fuente':
      return tasasCopy.sort((a, b) => (a.fuente || '').localeCompare(b.fuente || ''));
    case 'usuario':
      return tasasCopy.sort((a, b) => (a.usuarioNombre || '').localeCompare(b.usuarioNombre || ''));
    default:
      return tasasCopy;
  }
};

/**
 * Generar datos para gráfico de evolución de tasas
 * @param {Array} tasas - Lista de tasas ordenadas por fecha
 * @param {string} fuente - Fuente específica (opcional)
 * @returns {Object} Datos para gráfico
 */
export const generarDatosGrafico = (tasas, fuente = null) => {
  // Filtrar por fuente si se especifica
  let tasasFiltradas = tasas;
  if (fuente) {
    tasasFiltradas = tasas.filter(t => t.fuente === fuente);
  }
  
  // Ordenar por fecha ascendente para el gráfico
  tasasFiltradas.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  
  const labels = tasasFiltradas.map(t => {
    const fecha = new Date(t.fecha);
    return fecha.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  });
  
  const datosCompra = tasasFiltradas.map(t => t.tasaCompra || 0);
  const datosVenta = tasasFiltradas.map(t => t.tasaVenta || 0);
  
  return {
    labels,
    datasets: [
      {
        label: 'Tasa Compra',
        data: datosCompra,
        borderColor: '#4F46E5',
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Tasa Venta',
        data: datosVenta,
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar tasas a Excel
 * @param {Array} tasas - Lista de tasas
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (tasas, filename = 'tasas_cambio') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar dinámicamente para evitar problemas de SSR
    const XLSX = require('xlsx');
    
    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Preparar los datos
    const data = tasas.map(tasa => ({
      'ID': tasa.idTasa || '',
      'Fecha': formatearFechaTasa(tasa.fecha, true),
      'Tasa Compra': formatearMonto(tasa.tasaCompra, 4),
      'Tasa Venta': formatearMonto(tasa.tasaVenta, 4),
      'Spread': formatearMonto((tasa.tasaVenta || 0) - (tasa.tasaCompra || 0), 4),
      'Fuente': tasa.fuente || '',
      'Estado': convertirEstadoATexto(tasa.activa),
      'Usuario': tasa.usuarioNombre || '',
      'Observaciones': tasa.observaciones || '',
      'Fecha Creación': formatearFechaTasa(tasa.fechaCreacion, true)
    }));
    
    // Convertir a hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Establecer anchos de columna
    const wscols = [
      { wch: 8 },   // ID
      { wch: 20 },  // Fecha
      { wch: 15 },  // Tasa Compra
      { wch: 15 },  // Tasa Venta
      { wch: 12 },  // Spread
      { wch: 15 },  // Fuente
      { wch: 12 },  // Estado
      { wch: 25 },  // Usuario
      { wch: 40 },  // Observaciones
      { wch: 20 }   // Fecha Creación
    ];
    ws['!cols'] = wscols;
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Tasas de Cambio');
    
    // Agregar hoja de estadísticas
    const estadisticas = obtenerEstadisticasTasas(tasas);
    const statsData = [
      ['ESTADÍSTICAS DE TASAS DE CAMBIO', ''],
      ['Total Tasas', estadisticas.total],
      ['Tasas Activas', estadisticas.activas],
      ['Tasas Inactivas', estadisticas.inactivas],
      ['% Activas', `${estadisticas.porcentajeActivas}%`],
      ['% Inactivas', `${estadisticas.porcentajeInactivas}%`],
      ['Promedio Compra', formatearMonto(estadisticas.promedioCompra, 4)],
      ['Promedio Venta', formatearMonto(estadisticas.promedioVenta, 4)],
      ['Spread Promedio', formatearMonto(estadisticas.spreadPromedio, 4)],
      ['% Spread Promedio', `${estadisticas.porcentajeSpreadPromedio}%`],
      ['Máxima Compra', formatearMonto(estadisticas.maxCompra, 4)],
      ['Mínima Compra', formatearMonto(estadisticas.minCompra, 4)],
      ['Máxima Venta', formatearMonto(estadisticas.maxVenta, 4)],
      ['Mínima Venta', formatearMonto(estadisticas.minVenta, 4)],
      ['Tasas Recientes', estadisticas.recientes],
      ['% Recientes', `${estadisticas.porcentajeRecientes}%`],
      ['Usuarios Únicos', estadisticas.usuariosUnicos],
      ['', ''],
      ['DISTRIBUCIÓN POR FUENTE', 'CANTIDAD']
    ];
    
    // Agregar distribución por fuente
    Object.entries(estadisticas.porFuente).forEach(([fuente, cantidad]) => {
      statsData.push([fuente, cantidad]);
    });
    
    const wsStats = XLSX.utils.aoa_to_sheet(statsData);
    XLSX.utils.book_append_sheet(wb, wsStats, 'Estadísticas');
    
    // Generar nombre de archivo con fecha
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar el archivo
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: tasas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar tasas a PDF
 * @param {Array} tasas - Lista de tasas
 * @param {string} title - Título del documento
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (tasas, title = 'Reporte de Tasas de Cambio') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar jsPDF y el plugin
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    
    // Crear nuevo documento PDF
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Configuración del documento
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Logo/Encabezado
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 15, { align: 'center' });
    
    // Subtítulo con fecha
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generado: ${fechaStr}`, pageWidth / 2, 22, { align: 'center' });
    doc.text(`Total de tasas: ${tasas.length}`, pageWidth / 2, 28, { align: 'center' });
    
    // Estadísticas
    doc.setFontSize(12);
    doc.text('Resumen Estadístico', 14, 40);
    
    const estadisticas = obtenerEstadisticasTasas(tasas);
    const resumen = [
      `Total de tasas: ${estadisticas.total}`,
      `Tasas activas: ${estadisticas.activas} (${estadisticas.porcentajeActivas}%)`,
      `Tasas inactivas: ${estadisticas.inactivas} (${estadisticas.porcentajeInactivas}%)`,
      `Promedio compra: ${formatearMonto(estadisticas.promedioCompra, 4)}`,
      `Promedio venta: ${formatearMonto(estadisticas.promedioVenta, 4)}`,
      `Spread promedio: ${formatearMonto(estadisticas.spreadPromedio, 4)} (${estadisticas.porcentajeSpreadPromedio}%)`
    ];
    
    doc.setFontSize(10);
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, 16, 48 + (index * 5));
    });
    
    // Preparar datos para la tabla
    const tableData = tasas.map(tasa => [
      tasa.idTasa || '',
      formatearFechaTasa(tasa.fecha),
      formatearMonto(tasa.tasaCompra, 4),
      formatearMonto(tasa.tasaVenta, 4),
      formatearMonto((tasa.tasaVenta || 0) - (tasa.tasaCompra || 0), 4),
      tasa.fuente || '',
      convertirEstadoATexto(tasa.activa),
      tasa.usuarioNombre || '',
      formatearObservaciones(tasa.observaciones, 30)
    ]);
    
    // Crear tabla
    doc.autoTable({
      startY: 75,
      head: [['ID', 'Fecha', 'Compra', 'Venta', 'Spread', 'Fuente', 'Estado', 'Usuario', 'Observaciones']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229], // Indigo
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 15 },
        3: { cellWidth: 15 },
        4: { cellWidth: 12 },
        5: { cellWidth: 15 },
        6: { cellWidth: 12 },
        7: { cellWidth: 25 },
        8: { cellWidth: 35 }
      },
      margin: { top: 75 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        // Pie de página
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          'Jean Informatico C.A.',
          pageWidth - 10,
          pageHeight - 10,
          { align: 'right' }
        );
      }
    });
    
    // Generar nombre de archivo con fecha
    const fechaArchivo = fecha.toISOString().split('T')[0];
    const nombreArchivo = `reporte_tasas_${fechaArchivo}.pdf`;
    
    // Guardar el archivo
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Documento ${nombreArchivo} generado exitosamente`,
      count: tasas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} tasas - Lista de tasas
 * @param {string} baseFilename - Nombre base del archivo (sin extensión)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompleto = async (tasas, baseFilename = 'reporte_completo_tasas') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (tasas.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    // Exportar Excel
    const excelResult = exportarExcel(tasas, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    // Pequeño delay para evitar conflictos
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Exportar PDF
    const pdfResult = exportarPDF(tasas, `Reporte de Tasas de Cambio - Jean Informatico C.A.`);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: tasas.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerTasas,
  obtenerTasaPorId,
  obtenerTasaActiva,
  verificarTasaPorFechaFuente,
  obtenerTasasPorRangoFechas,
  obtenerTasasPorFuente,
  obtenerTasasPorUsuario,
  obtenerTasasPorFecha,
  crearTasa,
  actualizarTasa,
  eliminarTasa,
  activarTasa,
  desactivarTasa,
  cambiarEstadoTasa,
  convertirMoneda,
  convertirMonedaPersonalizado,
  obtenerResumenTasas,
  obtenerFuentesTasas,
  
  // Funciones mejoradas para editar y eliminar
  editarTasaConDesactivacion,
  eliminarTasaConVerificacion,
  
  // Funciones auxiliares
  formatearFechaTasa,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearMonto,
  formatearTasa,
  calcularDiferenciaPorcentual,
  formatearDiferencia,
  formatearObservaciones,
  buscarTasas,
  filtrarTasasPorEstado,
  filtrarTasasPorFuente,
  filtrarTasasPorUsuario,
  filtrarTasasPorFecha,
  obtenerEstadisticasTasas,
  ordenarTasas,
  generarDatosGrafico,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
};